import type { FilterModelMap } from "~/helpers/grid/filterModelToText";

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export const ACTIVE_GRID_TARGET = "__fernie_active_grid__";

export interface GridFilterValue {
	label: string;
	value: string;
}

export interface GridColumnSchema {
	field: string;
	filterType: string;
	filterValues?: GridFilterValue[];
	headerName: string;
	hidden?: boolean;
	param?: string;
}

export type FernieEntityType = "customer" | "masterOrder" | "order" | "purchaseOrder";

export interface FernieEntityContext {
	displayName?: string;
	entityId: string;
	entityType: FernieEntityType;
}

export type FernieAction =
	| { gridId: string; filterModel: FilterModelMap; type: "applyGridFilter" }
	| { gridId: string; type: "clearGridFilter" }
	| { gridId: string; searchText: string; type: "setGridSearch" }
	| { path: string; reachedDestination?: string; requestedDestination?: string; type: "navigateTo" }
	| {
			jobNumber: number;
			path: string;
			reachedDestination?: string;
			requestedDestination?: string;
			type: "openSchedulePanel";
	  }
	| {
			path: string;
			reachedDestination?: string;
			reportName: string;
			requestedDestination?: string;
			type: "openReport";
	  }
	| { panel: string; type: "openPanel" }
	| { panel: string; type: "closePanel" }
	| { outcome?: "abandoned" | "completed"; tourId: string; type: "startWalkthrough" };

interface FernieActionState {
	activeEntity: FernieEntityContext | null;
	activeGridDataKey: string | null;
	activeGridSchema: GridColumnSchema[] | null;
	executedActions: FernieAction[];
	pendingActions: FernieAction[];
}

const initialState: FernieActionState = {
	activeEntity: null,
	activeGridDataKey: null,
	activeGridSchema: null,
	executedActions: [],
	pendingActions: []
};

export const fernieActionSlice = createSlice({
	initialState,
	name: "fernieAction",
	reducers: {
		resetActionQueues: (state) => {
			state.pendingActions = [];
			state.executedActions = [];
		},
		dequeueAction: (state) => {
			state.pendingActions.shift();
		},
		enqueueActions: (state, action: PayloadAction<FernieAction[]>) => {
			state.pendingActions.push(...action.payload);
		},
		recordExecutedAction: (state, action: PayloadAction<FernieAction>) => {
			state.executedActions.push(action.payload);
		},
		registerActiveGrid: (state, action: PayloadAction<{ gridDataKey: string; schema: GridColumnSchema[] }>) => {
			state.activeGridDataKey = action.payload.gridDataKey;
			state.activeGridSchema = action.payload.schema;
		},
		unregisterActiveGrid: (state) => {
			state.activeGridDataKey = null;
			state.activeGridSchema = null;
		},
		registerActiveEntity: (state, action: PayloadAction<FernieEntityContext>) => {
			state.activeEntity = action.payload;
		},
		unregisterActiveEntity: (state) => {
			state.activeEntity = null;
		}
	}
});

export const {
	resetActionQueues,
	dequeueAction,
	enqueueActions,
	recordExecutedAction,
	registerActiveGrid,
	unregisterActiveGrid,
	registerActiveEntity,
	unregisterActiveEntity
} = fernieActionSlice.actions;

export default fernieActionSlice.reducer;
